/**
 * PKCE primitives — RFC 7636.
 *
 * Hand-rolled rather than pulled from a library on purpose: this build is Vue
 * CLI 5 / webpack with `@vue/compat` in MODE 2 and a pinned vue-loader, and
 * adding a dependency to it is a real risk. The whole mechanism is sixty lines
 * of Web Crypto, all of it standard.
 */

/** Cryptographically random, URL-safe, 43-128 chars per the RFC. */
export function createCodeVerifier(length = 64) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * S256 challenge. `plain` is never used — an intercepted authorization code is
 * only protected if the verifier cannot be derived from the challenge.
 */
export async function createCodeChallenge(verifier) {
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

/** Opaque random value for the `state` and `nonce` parameters. */
export function createRandomValue(length = 32) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

function base64UrlEncode(bytes) {
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Decodes a JWT payload WITHOUT verifying it.
 *
 * Only ever used on the id_token, and only for claims the client renders
 * (name, role, organization). The client is not a security boundary: the API
 * verifies every token it is given. Never use this to decide what a user may
 * do — ask the API.
 */
export function decodeJwtPayload(token) {
  const parts = String(token || '').split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(decodeURIComponent(escape(window.atob(padded))));
  } catch (e) {
    return null;
  }
}
